import { Button } from "@/components/ui/button";
import { trackOutboundClick } from "@/lib/analytics";

interface SubstackCTAProps {
  variant?: "card" | "inline" | "footer";
  location: string;
  className?: string;
  onDark?: boolean;
}

const SUBSTACK_URL = "https://kinshipcarenotes.substack.com";
const SUBSTACK_NAME = "Kinship Care Collective";

const SubstackIcon = ({ className = "h-5 w-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
  </svg>
);

const handleClick = (location: string) => {
  trackOutboundClick(SUBSTACK_URL, location);
};

export const SubstackCTA = ({ variant = "card", location, className = "", onDark = false }: SubstackCTAProps) => {
  if (variant === "inline") {
    return (
      <a
        href={SUBSTACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 text-sm font-medium hover:underline ${
          onDark ? "text-primary-foreground underline-offset-4" : "text-primary"
        } ${className}`}
        onClick={() => handleClick(location)}
      >
        <SubstackIcon className="h-4 w-4" />
        Follow on Substack
      </a>
    );
  }

  if (variant === "footer") {
    return (
      <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 ${className}`}>
        <div className="flex-1">
          <p className="text-sm font-medium text-primary-foreground mb-1">
            Prefer to read on Substack?
          </p>
          <p className="text-sm text-primary-foreground/70">
            Get caregiver resources, local Nashville notes, and honest reflections delivered to your inbox.
          </p>
        </div>
        <Button
          asChild
          variant="outline"
          className="shrink-0 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
        >
          <a
            href={SUBSTACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleClick(location)}
          >
            <SubstackIcon className="h-4 w-4 mr-2" />
            Follow {SUBSTACK_NAME}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-border bg-card p-6 md:p-8 ${className}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
          <SubstackIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-heading font-semibold mb-2">
            Subscribe, follow, and engage with our growing Substack
          </h3>
          <p className="text-muted-foreground leading-relaxed">
            {SUBSTACK_NAME} at <span className="font-medium text-foreground">@kinshipcarenotes</span> —
            caregiver resources, Nashville aging updates, and the conversations we’re having with families like yours.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Button asChild size="lg" className="whitespace-nowrap">
            <a
              href={SUBSTACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleClick(location)}
            >
              <SubstackIcon className="h-4 w-4 mr-2" />
              Subscribe
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="whitespace-nowrap"
          >
            <a
              href={`${SUBSTACK_URL}/archive`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleClick(`${location}_archive`)}
            >
              Read past issues
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubstackCTA;
